/**
 * 선택된 사진의 메타데이터(장소/위치/대상/관리번호/공종) 표시/편집 섹션
 * - canEdit=false: 읽기 전용 텍스트 표시 (메타 없으면 숨김)
 * - canEdit=true: SearchableSelect으로 선택 가능, 메타 없어도 항상 표시
 */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { equipmentDisplayName } from '@safetyhub/shared';
import { SearchableSelect } from './SearchableSelect';
import { useDeviceLayout } from '../../../hooks/useDeviceLayout';
import {
  getSiteLocations,
  getSiteDetailLocations,
  getSiteEquipment,
  getSiteProcessTypes,
  createSiteLocation,
  createSiteDetailLocation,
  createSiteEquipment,
  createSiteProcessType,
  updateSiteEquipment,
  flattenProcessTypeHierarchy,
  type SiteEquipment,
} from '../../../lib/site-metadata';
import { updatePhotoMetadata } from '../../../lib/inspections';
import { resolvePhotoProcessType } from '../../../lib/workCategory';

interface PhotoMeta {
  location?: { id: string; name: string } | null;
  detailLocation?: { id: string; name: string } | null;
  equipment?: { id: string; name: string; managementNumber?: string | null; processType?: string | null } | null;
  processType?: string | null;
}

interface PhotoMetaSectionProps {
  photo: PhotoMeta & { id: string };
  canEdit?: boolean;
  companyId?: string;
  siteId?: string;
  inspectionId?: string;
  onPhotoMetaUpdate?: (photoId: string, updates: {
    location?: { id: string; name: string } | null;
    detailLocation?: { id: string; name: string } | null;
    equipment?: { id: string; name: string; managementNumber?: string | null; processType?: string | null } | null;
    processType?: string | null;
  }) => void;
}

export function PhotoMetaSection({
  photo,
  canEdit,
  companyId,
  siteId,
  inspectionId,
  onPhotoMetaUpdate,
}: PhotoMetaSectionProps) {
  const { location, detailLocation, equipment } = photo;
  // 사진 레벨 공정은 workCategory 사다리 정본을 쓴다 — `||` 직접 체인은 공백만인 값을
  // 걸러 주긴 하지만 trim을 안 해 화면에 공백이 그대로 나가고, 무엇보다 이견 배지
  // (getWorkCategoryDissent)와 같은 함수를 쓰지 않으면 표시와 판정이 갈라질 수 있다.
  const effectiveProcessType = resolvePhotoProcessType({
    photoProcessType: photo.processType,
    equipmentProcessType: equipment?.processType ?? null,
  });

  // 읽기 전용 모드: 기존 동작 유지
  if (!canEdit) {
    const hasLocation = !!(location?.name || detailLocation?.name);
    const hasEquipment = !!(equipment?.name);
    const hasManagementNumber = !!(equipment?.managementNumber);
    const hasProcessType = !!effectiveProcessType;
    const hasAnyMeta = hasLocation || hasEquipment || hasManagementNumber || hasProcessType;

    if (!hasAnyMeta) return null;

    const locationPart = [location?.name, detailLocation?.name].filter(Boolean).join(' · ');

    return (
      <div className="flex items-center gap-1.5 px-3 py-2 bg-surface-soft border border-line rounded-t-lg text-sm text-ink-600 flex-wrap">
        {hasEquipment && (
          <>
            <span>{equipment!.name}</span>
            {hasManagementNumber && (
              <span className="text-ink-400 text-xs">({equipment!.managementNumber})</span>
            )}
          </>
        )}
        {hasProcessType && (
          <>
            {hasEquipment && <span className="text-ink-300">|</span>}
            <span>{effectiveProcessType}</span>
          </>
        )}
        {locationPart && (
          <>
            {(hasEquipment || hasProcessType) && <span className="text-ink-300">|</span>}
            <svg className="w-3.5 h-3.5 text-ink-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="font-medium text-ink-700">{locationPart}</span>
          </>
        )}
      </div>
    );
  }

  // 편집 모드
  return (
    <PhotoMetaEditSection
      photo={photo}
      companyId={companyId!}
      siteId={siteId!}
      inspectionId={inspectionId!}
      onPhotoMetaUpdate={onPhotoMetaUpdate}
    />
  );
}

// ---- 편집 모드 전용 컴포넌트 ----

interface PhotoMetaEditSectionProps {
  photo: PhotoMeta & { id: string };
  companyId: string;
  siteId: string;
  inspectionId: string;
  onPhotoMetaUpdate?: PhotoMetaSectionProps['onPhotoMetaUpdate'];
}

function PhotoMetaEditSection({
  photo,
  companyId,
  siteId,
  inspectionId,
  onPhotoMetaUpdate,
}: PhotoMetaEditSectionProps) {
  const queryClient = useQueryClient();
  const deviceLayout = useDeviceLayout();
  const isDesktop = deviceLayout === 'desktop';

  const [locationId, setLocationId] = useState(photo.location?.id ?? '');
  const [detailLocationId, setDetailLocationId] = useState(photo.detailLocation?.id ?? '');
  const [equipmentId, setEquipmentId] = useState(photo.equipment?.id ?? '');
  const [photoProcessType, setPhotoProcessType] = useState<string | null>(photo.processType ?? null);

  // photo 변경 시 로컬 상태 동기화
  useEffect(() => {
    setLocationId(photo.location?.id ?? '');
    setDetailLocationId(photo.detailLocation?.id ?? '');
    setEquipmentId(photo.equipment?.id ?? '');
    setPhotoProcessType(photo.processType ?? null);
  }, [photo.id, photo.location?.id, photo.detailLocation?.id, photo.equipment?.id, photo.processType]);

  const { data: locations = [] } = useQuery({
    queryKey: ['site-locations', companyId, siteId],
    queryFn: () => getSiteLocations(companyId, siteId),
    enabled: !!(companyId && siteId),
    staleTime: 60_000,
  });

  const { data: detailLocations = [] } = useQuery({
    queryKey: ['site-detail-locations', companyId, siteId],
    queryFn: () => getSiteDetailLocations(companyId, siteId),
    enabled: !!(companyId && siteId),
    staleTime: 60_000,
  });

  const { data: equipmentList = [], refetch: refetchEquipment } = useQuery({
    queryKey: ['site-equipment', companyId, siteId],
    queryFn: () => getSiteEquipment(companyId, siteId),
    enabled: !!(companyId && siteId),
    staleTime: 60_000,
  });

  const { data: processTypes = [] } = useQuery({
    queryKey: ['site-process-types', companyId, siteId],
    queryFn: () => getSiteProcessTypes(companyId, siteId),
    enabled: !!(companyId && siteId),
    staleTime: 60_000,
  });

  const activeLocations = useMemo(() => locations.filter((l) => l.isActive), [locations]);
  const activeDetailLocations = useMemo(() => detailLocations.filter((l) => l.isActive), [detailLocations]);
  const activeEquipment = useMemo(() => equipmentList.filter((e) => e.isActive), [equipmentList]);
  const activeProcessTypes = useMemo(() => processTypes.filter((pt) => pt.isActive), [processTypes]);

  const locationOptions = useMemo(() => activeLocations.map((l) => ({ id: l.id, name: l.name })), [activeLocations]);
  const detailLocationOptions = useMemo(() => activeDetailLocations.map((l) => ({ id: l.id, name: l.name })), [activeDetailLocations]);

  // 계층 구조를 평탄화 (자식 있는 부모는 옵션에서 제외, "부모 > 자식" 라벨)
  const processTypeOptions = useMemo(
    () => flattenProcessTypeHierarchy(processTypes),
    [processTypes],
  );

  const equipmentOptions = useMemo(
    () =>
      activeEquipment.map((eq) => ({
        id: eq.id,
        // 하위 설비는 「부모 › 이름」(⑥ ⓑ — 셀마다 같은 이름의 로봇이 있다) · 이름 짓기는 shared 한 벌
        name: equipmentDisplayName(eq),
      })),
    [activeEquipment],
  );

  const selectedEquipment = useMemo(
    () => activeEquipment.find((e) => e.id === equipmentId) ?? null,
    [activeEquipment, equipmentId],
  );

  const callUpdateApi = useCallback(
    async (patch: { locationId?: string | null; equipmentId?: string | null; detailLocationId?: string | null; processType?: string | null }) => {
      if (!inspectionId || !companyId) return;
      try {
        const result = await updatePhotoMetadata(inspectionId, photo.id, companyId, patch);
        if (result.success && result.data) {
          onPhotoMetaUpdate?.(photo.id, {
            location: result.data.location ?? null,
            detailLocation: result.data.detailLocation ?? null,
            equipment: result.data.equipment ?? null,
            processType: result.data.processType ?? null,
          });
        }
      } catch {
        // fire-and-forget: 실패 시 무시
      }
    },
    [inspectionId, photo.id, companyId, onPhotoMetaUpdate],
  );

  const handleLocationChange = (id: string) => {
    setLocationId(id);
    callUpdateApi({ locationId: id || null });
    // 대상이 선택된 상태면 대상의 currentLocationId도 업데이트
    if (equipmentId) {
      updateSiteEquipment(companyId, siteId, equipmentId, { currentLocationId: id || null }).catch(() => {});
    }
  };

  const handleDetailLocationChange = (id: string) => {
    setDetailLocationId(id);
    callUpdateApi({ detailLocationId: id || null });
    // 대상이 선택된 상태면 대상의 currentDetailLocationId도 업데이트
    if (equipmentId) {
      updateSiteEquipment(companyId, siteId, equipmentId, { currentDetailLocationId: id || null }).catch(() => {});
    }
  };

  const handleEquipmentChange = (id: string) => {
    setEquipmentId(id);
    const updates: Record<string, string | null> = { equipmentId: id || null };
    if (id) {
      // 대상에 연결된 장소/위치가 있으면 자동 채움
      const selected = activeEquipment.find((e) => e.id === id);
      if (selected?.currentLocationId) {
        setLocationId(selected.currentLocationId);
        updates.locationId = selected.currentLocationId;
      }
      if (selected?.currentDetailLocationId) {
        setDetailLocationId(selected.currentDetailLocationId);
        updates.detailLocationId = selected.currentDetailLocationId;
      }
    } else {
      // 대상 해제 시 장소/상세위치도 함께 해제
      setLocationId('');
      setDetailLocationId('');
      updates.locationId = null;
      updates.detailLocationId = null;
    }
    callUpdateApi(updates);
  };

  const handleAddLocation = async (name: string): Promise<string> => {
    const created = await createSiteLocation(companyId, siteId, { name });
    await queryClient.invalidateQueries({ queryKey: ['site-locations', companyId, siteId] });
    return created.id;
  };

  const handleAddDetailLocation = async (name: string): Promise<string> => {
    const created = await createSiteDetailLocation(companyId, siteId, { name });
    await queryClient.invalidateQueries({ queryKey: ['site-detail-locations', companyId, siteId] });
    return created.id;
  };

  const handleAddEquipment = async (name: string): Promise<string> => {
    const created = await createSiteEquipment(companyId, siteId, { name });
    await refetchEquipment();
    return created.id;
  };

  const handleEquipmentUpdated = (updated: SiteEquipment) => {
    queryClient.setQueryData(
      ['site-equipment', companyId, siteId],
      (old: SiteEquipment[] | undefined) =>
        old ? old.map((e) => (e.id === updated.id ? updated : e)) : [updated],
    );
  };

  return (
    <div className="px-3 pt-2 pb-2 bg-surface-soft border border-line rounded-t-lg">
      {/* 대상 / 관리번호 / 공종 행 */}
      <div className="flex gap-2 flex-wrap items-end mb-1.5">
        <SearchableSelect
          value={equipmentId}
          onChange={handleEquipmentChange}
          options={equipmentOptions}
          placeholder="대상 선택"
          onAdd={handleAddEquipment}
          sheetMode={!isDesktop}
        />
        {selectedEquipment ? (
          <EquipmentMetaInline
            equipment={selectedEquipment}
            companyId={companyId}
            siteId={siteId}
            onUpdated={handleEquipmentUpdated}
          />
        ) : (
          <div className="flex-1 min-w-[60px]">
            <input
              type="text"
              disabled
              placeholder="관리번호"
              className="w-full px-2 py-1.5 text-xs border border-line rounded-control bg-ink-100 text-ink-400 cursor-not-allowed"
            />
          </div>
        )}
        <SearchableSelect
          value={activeProcessTypes.find((pt) => pt.name === (selectedEquipment?.processType ?? photoProcessType))?.id ?? ''}
          onChange={async (v) => {
            if (!v) {
              setPhotoProcessType(null);
              callUpdateApi({ processType: null });
              if (selectedEquipment) {
                try {
                  const updated = await updateSiteEquipment(companyId, siteId, selectedEquipment.id, { processType: null });
                  handleEquipmentUpdated(updated);
                } catch { /* 무시 */ }
              }
              return;
            }
            const selected = activeProcessTypes.find((pt) => pt.id === v);
            if (!selected) return; // onAdd에서 이미 처리됨
            setPhotoProcessType(selected.name);
            callUpdateApi({ processType: selected.name });
            if (selectedEquipment) {
              try {
                const updated = await updateSiteEquipment(companyId, siteId, selectedEquipment.id, { processType: selected.name });
                handleEquipmentUpdated(updated);
              } catch { /* 무시 */ }
            }
          }}
          options={processTypeOptions}
          placeholder="공종/공정 선택"
          onAdd={async (name) => {
            const created = await createSiteProcessType(companyId, siteId, { name });
            queryClient.invalidateQueries({ queryKey: ['site-process-types', companyId, siteId] });
            setPhotoProcessType(name);
            callUpdateApi({ processType: name });
            if (selectedEquipment) {
              try {
                const updated = await updateSiteEquipment(companyId, siteId, selectedEquipment.id, { processType: name });
                handleEquipmentUpdated(updated);
              } catch { /* 무시 */ }
            }
            return created.id;
          }}
          sheetMode={!isDesktop}
        />
      </div>
      {/* 장소 / 위치 행 */}
      <div className="flex gap-2 flex-wrap">
        <SearchableSelect
          value={locationId}
          onChange={handleLocationChange}
          options={locationOptions}
          placeholder="장소 선택"
          onAdd={handleAddLocation}
          sheetMode={!isDesktop}
        />
        <SearchableSelect
          value={detailLocationId}
          onChange={handleDetailLocationChange}
          options={detailLocationOptions}
          placeholder="상세위치 선택"
          onAdd={handleAddDetailLocation}
          sheetMode={!isDesktop}
        />
      </div>
    </div>
  );
}

// ---- 대상 관리번호/공종 인라인 편집 ----

interface EquipmentMetaInlineProps {
  equipment: SiteEquipment;
  companyId: string;
  siteId: string;
  onUpdated: (updated: SiteEquipment) => void;
}

function EquipmentMetaInline({ equipment, companyId, siteId, onUpdated }: EquipmentMetaInlineProps) {
  const [managementNumber, setManagementNumber] = useState(equipment.managementNumber ?? '');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, []);

  useEffect(() => {
    setManagementNumber(equipment.managementNumber ?? '');
  }, [equipment.id, equipment.managementNumber]);

  const scheduleSave = (patch: { managementNumber?: string }) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const updated = await updateSiteEquipment(companyId, siteId, equipment.id, patch);
        onUpdated(updated);
      } catch {
        // 저장 실패 시 무시
      }
    }, 600);
  };

  return (
    <div className="flex-1 min-w-[60px]">
      <input
        type="text"
        value={managementNumber}
        onChange={(e) => {
          setManagementNumber(e.target.value);
          scheduleSave({ managementNumber: e.target.value || undefined });
        }}
        placeholder="관리번호"
        className="w-full px-2 py-1.5 text-xs border border-line rounded-control focus:outline-none focus:ring-1 focus:ring-brand-500 bg-white"
      />
    </div>
  );
}
